import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { eventsAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import MediaPicker from '../components/MediaPicker';
import { ArrowLeft, Plus, Upload, Trash2, Pencil, Star, Calendar, MapPin, Library, FileSpreadsheet, Download, Repeat, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  title: '', start_date: '', end_date: '', venue: '', address: '',
  description: '', image_url: '', link: '', category: 'general',
  featured: false, published: true, slug: '',
};

const EMPTY_RECUR = { mode: 'none', count: 4, dates: '' };

const CATEGORIES = ['general', 'music', 'family', 'sport', 'food', 'community', 'festival', 'theatre', 'market', 'charity'];

const EventsManager = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = new, otherwise event object
  const [form, setForm] = useState(EMPTY);
  const [recur, setRecur] = useState(EMPTY_RECUR);
  const [recurPreview, setRecurPreview] = useState(null); // {count, occurrences} | null
  const [previewBusy, setPreviewBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [cleanBusy, setCleanBusy] = useState(false);
  const csvRef = useRef(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await eventsAPI.adminList();
      // Sort by start_date desc (upcoming first then past)
      const now = new Date();
      const sorted = (r.data || []).sort((a, b) => {
        const da = new Date(a.start_date);
        const db = new Date(b.start_date);
        const aFuture = da >= now;
        const bFuture = db >= now;
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        return da - db;
      });
      setEvents(sorted);
    } catch (e) {
      toast({ title: 'Failed to load events', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupStale = async () => {
    // Two-step: preview counts, then confirm hard delete. Ticketmaster and
    // Skiddle events are always kept — server enforces this too.
    setCleanBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const previewRes = await fetch(`${BACKEND_URL}/api/admin/events/cleanup-stale?dry_run=true`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const preview = await previewRes.json();
      const n = preview.would_delete || 0;
      if (n === 0) {
        toast({ title: 'Nothing to clean up', description: 'Only Ticketmaster and Skiddle events remain.' });
        return;
      }
      const breakdown = Object.entries(preview.by_source)
        .map(([k, v]) => `  • ${k}: ${v}`).join('\n');
      const ok = window.confirm(
        `Delete ${n} stale event${n === 1 ? '' : 's'}?\n\n${breakdown}\n\nTicketmaster and Skiddle events will be kept. This cannot be undone.`
      );
      if (!ok) return;
      const doRes = await fetch(`${BACKEND_URL}/api/admin/events/cleanup-stale?dry_run=false`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const done = await doRes.json();
      toast({ title: `Deleted ${done.deleted} events`, description: 'The events list is now Ticketmaster + Skiddle only.' });
      load();
    } catch (e) {
      toast({ title: 'Cleanup failed', description: e.message, variant: 'destructive' });
    } finally {
      setCleanBusy(false);
    }
  };

  const startNew = () => {
    setEditing({});
    setForm(EMPTY);
    setRecur(EMPTY_RECUR);
    setRecurPreview(null);
  };

  const startEdit = (ev) => {
    setEditing(ev);
    setForm({
      ...EMPTY,
      ...ev,
      start_date: ev.start_date ? ev.start_date.slice(0, 16) : '',
      end_date: ev.end_date ? ev.end_date.slice(0, 16) : '',
    });
    setRecur(EMPTY_RECUR);
    setRecurPreview(null);
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); setRecur(EMPTY_RECUR); setRecurPreview(null); };

  const buildRecurrencePayload = () => {
    if (recur.mode === 'none') return null;
    if (recur.mode === 'custom') {
      const dates = recur.dates.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      return { mode: 'custom', dates };
    }
    return { mode: recur.mode, count: Math.max(2, Math.min(52, Number(recur.count) || 2)) };
  };

  const previewRecurrence = async () => {
    if (!form.title.trim() || !form.start_date) {
      toast({ title: 'Set a title and start date first', variant: 'destructive' });
      return;
    }
    const rp = buildRecurrencePayload();
    if (!rp) return;
    setPreviewBusy(true);
    try {
      const r = await eventsAPI.previewRecurring({
        title: form.title, start_date: form.start_date, end_date: form.end_date || null,
        recurrence: rp,
      });
      setRecurPreview(r.data);
    } catch (e) {
      toast({ title: 'Preview failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      setRecurPreview(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_date) {
      toast({ title: 'Title and start date are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || undefined,
        end_date: form.end_date || null,
      };
      const rp = editing && !editing.id ? buildRecurrencePayload() : null;
      if (rp) {
        const r = await eventsAPI.generateRecurring({ ...payload, recurrence: rp });
        toast({ title: `Series created`, description: `${r.data.created} events generated` });
      } else if (editing && editing.id) {
        await eventsAPI.update(editing.id, payload);
        toast({ title: 'Event updated' });
      } else {
        await eventsAPI.create(payload);
        toast({ title: 'Event created' });
      }
      cancelEdit();
      load();
    } catch (e2) {
      toast({ title: 'Save failed', description: e2.response?.data?.detail || e2.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await eventsAPI.delete(ev.id);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const handleCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await eventsAPI.importCSV(fd);
      const { created, skipped = 0, errors = [] } = r.data;
      const parts = [`${created} created`];
      if (skipped) parts.push(`${skipped} skipped (duplicate title + date)`);
      if (errors.length) parts.push(`${errors.length} errors`);
      toast({
        title: `Events imported`,
        description: parts.join(' · '),
      });
      if (errors.length) console.warn('CSV import errors:', errors);
      if (skipped) console.info('CSV duplicates skipped:', r.data.skipped_items);
      load();
    } catch (e) {
      toast({ title: 'CSV import failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setCsvBusy(false);
      if (csvRef.current) csvRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'title,start_date,end_date,venue,address,description,image_url,link,category,featured,published\n' +
                "Sma Shot Day Festival,2026-07-04T11:00,2026-07-04T18:00,Paisley Town Centre,High Street,Annual Sma' Shot Day weekender,,https://paisley.org.uk,festival,true,true\n" +
                'Paisley Halloween Parade,2026-10-31T19:00,,Paisley Cross,Paisley Cross PA1,Family-friendly parade,,,family,false,true\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'paisley-events-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm"><ArrowLeft size={16} className="mr-2" />Dashboard</Button>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">Events Manager</h1>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="csv-template-btn">
              <Download size={14} className="mr-2" />CSV template
            </Button>
            <input
              ref={csvRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleCsv(e.target.files?.[0])}
            />
            <Button type="button" variant="outline" disabled={csvBusy} onClick={() => csvRef.current?.click()} data-testid="csv-import-btn"
              title="Needs a title column and a date column. Accepts date + time separately, UK dates like 01/10/2026, venue, city, price, link and summary.">
              <FileSpreadsheet size={14} className="mr-2" />{csvBusy ? 'Importing…' : 'Import CSV'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={cleanBusy}
              onClick={cleanupStale}
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="cleanup-stale-btn"
              title="Delete events left behind by automated feeds we no longer run. Your own events — typed in, CSV-imported, community-submitted or recurring — are kept."
            >
              <Sparkles size={14} className="mr-2" />{cleanBusy ? 'Cleaning…' : 'Clean up stale'}
            </Button>
            <Button type="button" onClick={startNew} className="bg-cyan-500 hover:bg-cyan-600" data-testid="new-event-btn">
              <Plus size={14} className="mr-2" />New event
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_400px] gap-6">
        {/* List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>{events.length} events total</span>
            <span className="text-xs text-gray-500 font-normal">Sorted by upcoming first</span>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : events.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 mb-4">No events yet. Add your first one or import a CSV.</p>
              <Button onClick={startNew} className="bg-cyan-500 hover:bg-cyan-600">
                <Plus size={14} className="mr-2" />Create first event
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="events-admin-list">
              {events.map((ev) => {
                const isFuture = new Date(ev.start_date) >= new Date();
                return (
                  <li key={ev.id} className="px-5 py-4 hover:bg-slate-50 flex items-center gap-4">
                    <div className="shrink-0 w-14 text-center bg-slate-100 rounded-lg py-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                        {new Date(ev.start_date).toLocaleString('en-GB', { month: 'short' })}
                      </p>
                      <p className="text-xl font-bold leading-tight text-slate-900">
                        {new Date(ev.start_date).getDate()}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                        {ev.featured && <Star size={12} className="text-fuchsia-500 fill-fuchsia-500" />}
                        {ev.title}
                        {!ev.published && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1">DRAFT</span>}
                        {!isFuture && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded ml-1">PAST</span>}
                      </p>
                      <p className="text-xs text-gray-500 truncate flex items-center gap-2 mt-0.5">
                        {ev.venue && (<><MapPin size={10} />{ev.venue} ·</>)}
                        <span className="capitalize bg-slate-100 px-1.5 rounded">{ev.category}</span>
                        <span>· {ev.source}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Link to={`/events/${ev.slug}`} target="_blank" className="text-xs text-cyan-600 hover:underline mr-2">View</Link>
                      <button onClick={() => startEdit(ev)} className="p-2 text-gray-500 hover:text-cyan-600" data-testid={`edit-event-${ev.slug}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(ev)} className="p-2 text-gray-500 hover:text-red-600" data-testid={`delete-event-${ev.slug}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Editor panel */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 h-fit lg:sticky lg:top-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            {editing && editing.id ? 'Edit event' : editing ? 'Create event' : 'Select or create an event'}
          </h2>
          {!editing ? (
            <p className="text-sm text-gray-500">Click "New event" above or pick one from the list to edit.</p>
          ) : (
            <form onSubmit={save} className="space-y-4" data-testid="event-form">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required data-testid="event-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start *</Label>
                  <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} required data-testid="event-start-input" />
                </div>
                <div>
                  <Label>End</Label>
                  <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Venue</Label>
                <Input value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} placeholder="e.g. Paisley Town Hall" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Street, postcode" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What's happening, what to expect…" />
              </div>
              <div>
                <Label>Image</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="/api/files/… or https://…" className="flex-1" />
                  <Button type="button" variant="outline" onClick={() => setMediaOpen(true)} data-testid="event-image-library-btn">
                    <Library size={14} className="mr-1" />Library
                  </Button>
                </div>
                {form.image_url && (
                  <img src={form.image_url.startsWith('http') ? form.image_url : `${BACKEND_URL}${form.image_url}`} alt="" className="mt-2 h-32 w-full object-cover rounded-lg" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>External link</Label>
                  <Input value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} placeholder="Tickets / info URL" />
                </div>
              </div>
              <div>
                <Label>URL slug</Label>
                <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="auto-from-title" />
              </div>

              {/* Repeats — only shown when creating a new event, not when editing an existing one */}
              {editing && !editing.id && (
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid="event-repeats-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Repeat size={14} className="text-cyan-600" />
                    <Label className="mb-0 text-sm font-semibold">Repeats</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Cadence</Label>
                      <Select
                        value={recur.mode}
                        onValueChange={(v) => { setRecur((r) => ({ ...r, mode: v })); setRecurPreview(null); }}
                      >
                        <SelectTrigger data-testid="recur-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Doesn&apos;t repeat</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="fortnightly">Fortnightly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="custom">Custom dates</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(recur.mode === 'weekly' || recur.mode === 'fortnightly' || recur.mode === 'monthly') && (
                      <div>
                        <Label className="text-xs">How many occurrences (2–52)</Label>
                        <Input
                          type="number" min={2} max={52} value={recur.count}
                          onChange={(e) => { setRecur((r) => ({ ...r, count: e.target.value })); setRecurPreview(null); }}
                          data-testid="recur-count-input"
                        />
                      </div>
                    )}
                  </div>
                  {recur.mode === 'custom' && (
                    <div className="mt-3">
                      <Label className="text-xs">Dates (one per line, YYYY-MM-DD or ISO). Time inherits from Start above.</Label>
                      <Textarea
                        rows={4}
                        value={recur.dates}
                        onChange={(e) => { setRecur((r) => ({ ...r, dates: e.target.value })); setRecurPreview(null); }}
                        placeholder={'2026-08-01\n2026-08-15\n2026-09-05'}
                        data-testid="recur-dates-input"
                      />
                    </div>
                  )}
                  {recur.mode !== 'none' && (
                    <div className="mt-3">
                      <Button
                        type="button" variant="outline" size="sm"
                        disabled={previewBusy}
                        onClick={previewRecurrence}
                        data-testid="recur-preview-btn"
                      >
                        {previewBusy ? 'Building preview…' : 'Preview dates'}
                      </Button>
                      {recurPreview && (
                        <div className="mt-2 text-xs text-slate-700 bg-white border border-slate-200 rounded-md p-2 max-h-40 overflow-y-auto" data-testid="recur-preview-list">
                          <p className="font-semibold mb-1">Will create {recurPreview.count} events:</p>
                          <ul className="space-y-0.5">
                            {recurPreview.occurrences.map((o, i) => (
                              <li key={i} className="tabular-nums text-slate-600">
                                {new Date(o.start).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 rounded" data-testid="event-featured-checkbox" />
                  <span className="text-sm">Featured (hero treatment)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="w-4 h-4 rounded" />
                  <span className="text-sm">Published</span>
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 flex-1" data-testid="event-save-btn">
                  {saving ? 'Saving…' : (editing.id
                    ? 'Update event'
                    : (recur.mode !== 'none' && recurPreview
                        ? `Create ${recurPreview.count} events`
                        : 'Create event'))}
                </Button>
                <Button type="button" variant="outline" onClick={cancelEdit}>Cancel</Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <MediaPicker
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        title="Select event image"
        aiContext={{
          kind: 'event',
          title: form.title,
          context: [form.venue, form.address, form.start_date, form.description]
            .filter(Boolean).join(' · '),
        }}
        onSelect={(url) => setForm((f) => ({ ...f, image_url: url }))}
      />
    </div>
  );
};

export default EventsManager;
